import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useEffect, useRef, useState } from "react";
import { Head, useForm } from "@inertiajs/react";
import { AlertTriangle, CheckCircle2, Download, RotateCcw, Save } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SegmentedChoice from "@/components/shared/SegmentedChoice";
import { printModeOptions, marginOptions } from "@/lib/printLayout";

// Literal letter pages at 96 CSS px per inch. Must match the preview gap
// in pre_campaign_info_sheets/_head; the margins come from the sheet's
// settings (PrintLayout::MARGINS).
const PX_PER_INCH = 96;
const PAGE_WIDTH_PX = 8.5 * PX_PER_INCH;
const PAGE_HEIGHT_PX = 11 * PX_PER_INCH;
const GAP_PX = 32;
const PITCH_PX = PAGE_HEIGHT_PX + GAP_PX;

/**
 * Pre-campaign info sheet editor. Live preview of the real sheet on the
 * left — the server-rendered sheet HTML (same template as the PDF) for the
 * unsaved options, paginated into literal 8.5×11 pages — and the options
 * on the right: PagePledge's wording, prefilled and editable, toggles for
 * the generated blocks, and a section of the school's own — listed in the
 * order they print, so each option sits beside what it changes.
 *
 * A sheet is at most one piece of paper: two pages, printed both sides.
 */
export default function Edit({
  school, membership, campaign, audience, text_blocks, toggles, order, default_hidden, print_modes, margins, settings, limits,
  update_path, preview_path, pdf_path, back,
}) {
  const form = useForm({
    print_mode: settings.print_mode,
    margins: settings.margins,
    hidden: settings.hidden,
    text: Object.fromEntries(text_blocks.map((b) => [b.key, b.value])),
    custom_title: settings.custom_title,
    custom_body: settings.custom_body,
  });
  const { data, setData } = form;
  const [pages, setPages] = useState(1);
  const tooLong = pages > limits.pages;

  const setShown = (key, shown) =>
    setData("hidden", shown ? data.hidden.filter((k) => k !== key) : [...data.hidden, key]);

  const setText = (key, field, value) =>
    setData("text", { ...data.text, [key]: { ...data.text[key], [field]: value } });

  const setStep = (key, stepKey, field, value) => {
    const block = data.text[key];
    setData("text", {
      ...data.text,
      [key]: { ...block, steps: { ...block.steps, [stepKey]: { ...block.steps[stepKey], [field]: value } } },
    });
  };

  const textBlocksByKey = Object.fromEntries(text_blocks.map((b) => [b.key, b]));
  const togglesByKey = Object.fromEntries(toggles.map((t) => [t.key, t]));

  const renderToggle = (key) => {
    const hint = toggleHint(key, campaign);
    return (
      <label htmlFor={`toggle-${key}`} className="flex items-start gap-3 rounded-md px-2 py-1.5 hover:bg-secondary/40 cursor-pointer">
        <Checkbox
          id={`toggle-${key}`}
          checked={!data.hidden.includes(key)}
          onChange={(e) => setShown(key, e.target.checked)}
          className="mt-0.5 h-4 w-4"
        />
        <span className="min-w-0">
          <span className="block text-sm text-foreground">{togglesByKey[key].label}</span>
          {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
        </span>
      </label>
    );
  };

  const save = (onSuccess) => form.patch(update_path, { preserveScroll: true, onSuccess });

  // Download what's on screen: save first when there are unsaved changes.
  const download = () => {
    if (form.isDirty) {
      save(() => { window.location.href = pdf_path; });
    } else {
      window.location.href = pdf_path;
    }
  };

  const resetAll = () => {
    form.setData({
      print_mode: data.print_mode,
      margins: data.margins,
      hidden: default_hidden,
      text: Object.fromEntries(text_blocks.map((b) => [b.key, b.default])),
      custom_title: "",
      custom_body: "",
    });
  };


  return (
    <SchoolLayout
      school={school}
      membership={membership}
      back={back}
      title={`Pre-campaign info for ${audience}`}
      actions={
        // Kept in the header, outside the scrolling options panel, so the
        // actions are always in reach.
        <div className="flex flex-wrap items-center justify-end gap-2">
          {form.isDirty && !form.processing && (
            <span className="text-xs text-muted-foreground mr-1">Unsaved changes</span>
          )}
          <Button variant="ghost" size="sm" onClick={resetAll} className="text-muted-foreground">
            <RotateCcw className="h-4 w-4" />
            Reset all
          </Button>
          <Button variant="outline" size="sm" onClick={() => save()} disabled={!form.isDirty || form.processing || tooLong}>
            <Save className="h-4 w-4" />
            {form.processing ? "Saving…" : form.recentlySuccessful && !form.isDirty ? "Saved" : "Save changes"}
          </Button>
          {/* The page's one job: get the sheet out. Teachers' copy is emailed
              or printed by the admin, families' is printed. */}
          <Button onClick={download} disabled={form.processing || tooLong}>
            <Download className="h-4 w-4" />
            {form.isDirty ? "Save & download PDF" : "Download PDF"}
          </Button>
        </div>
      }
    >
      <Head title={`Pre-campaign info for ${audience} · ${campaign.name}`} />

      {(form.errors.settings || tooLong) && (
        <p className="mb-4 flex gap-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          {form.errors.settings || "Too long for one sheet of paper — shorten some text or turn off a section to save."}
        </p>
      )}

      {/* On large screens: a fixed-height workspace where the preview and the
          options scroll independently, so the preview never leaves view.
          (Sticky doesn't work here — SchoolLayout's <main> is an overflow
          container.) */}
      <div className="grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,1fr)_400px] lg:h-[calc(100vh-12rem)] lg:min-h-[560px]">
        <div className="min-h-0">
          <SheetPreview
            previewPath={preview_path}
            options={data}
            marginY={margins.find((m) => m.key === data.margins).vertical * PX_PER_INCH}
            maxPages={limits.pages}
            onPages={setPages}
          />
        </div>

        <Card className="min-h-0 lg:overflow-y-auto">
          <CardHeader>
            <CardTitle>Customize</CardTitle>
            <CardDescription>
              We've written everything for someone who's never heard of a read-a-thon. Edit any of it to suit your school.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <section className="space-y-4">
              <GroupLabel>Printing</GroupLabel>
              <SegmentedChoice
                label="Print mode"
                options={printModeOptions(print_modes)}
                value={data.print_mode}
                onChange={(key) => setData("print_mode", key)}
              />
              <SegmentedChoice
                label="Margins"
                options={marginOptions(margins)}
                value={data.margins}
                onChange={(key) => setData("margins", key)}
                note="Narrower margins fit more on each page."
              />
            </section>

            <section className="space-y-4">
              <GroupLabel>On the sheet, top to bottom</GroupLabel>
              {order.map((item) => {
                if (item.type === "custom") {
                  return (
                    <div key="custom" className="rounded-lg border border-border p-3 space-y-3">
                      <span className="text-sm font-medium">Your own section (optional)</span>
                      <div className="space-y-1.5">
                        <Label htmlFor="custom_title">Heading</Label>
                        <Input
                          id="custom_title"
                          value={data.custom_title}
                          maxLength={limits.heading}
                          placeholder={`More from ${school.name}`}
                          onChange={(e) => setData("custom_title", e.target.value)}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="custom_body">Text</Label>
                        <AutoTextarea
                          id="custom_body"
                          value={data.custom_body}
                          maxLength={limits.custom_body}
                          placeholder="Volunteer asks, a kick-off assembly, who to contact with questions…"
                          onChange={(value) => setData("custom_body", value)}
                        />
                        <p className="flex justify-between text-xs text-muted-foreground">
                          <span>Leave blank to skip.</span>
                          <span className="font-tabular">{data.custom_body.length}/{limits.custom_body}</span>
                        </p>
                      </div>
                    </div>
                  );
                }

                if (item.type === "toggle") {
                  return (
                    <div key={item.key} className="rounded-lg border border-border px-1 py-1.5">
                      {renderToggle(item.key)}
                    </div>
                  );
                }

                const block = textBlocksByKey[item.key];
                return (
                  <TextBlockEditor
                    key={block.key}
                    block={block}
                    value={data.text[block.key]}
                    shown={!data.hidden.includes(block.key)}
                    limits={limits}
                    onShow={(shown) => setShown(block.key, shown)}
                    onChange={(field, value) => setText(block.key, field, value)}
                    onStepChange={(stepKey, field, value) => setStep(block.key, stepKey, field, value)}
                    onReset={() => setData("text", { ...data.text, [block.key]: block.default })}
                  >
                    {item.extras && (
                      <div className="border-t border-border pt-2 -mx-1.5">
                        <p className="px-2 pb-1 text-xs text-muted-foreground">Add to the end of the first paragraph:</p>
                        {item.extras.map((key) => <div key={key}>{renderToggle(key)}</div>)}
                      </div>
                    )}
                  </TextBlockEditor>
                );
              })}
            </section>

            <p className="text-xs text-muted-foreground leading-relaxed">
              In paragraphs: a blank line starts a new paragraph. Start a line with “- ” for a bullet; “- Label: text” makes the label bold.
            </p>

          </CardContent>
        </Card>
      </div>
    </SchoolLayout>
  );
}

function GroupLabel({ children }) {
  return <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{children}</h3>;
}

function TextBlockEditor({ block, value, shown, limits, onShow, onChange, onStepChange, onReset, children }) {
  const edited = JSON.stringify(value) !== JSON.stringify(block.default);

  return (
    <div className="rounded-lg border border-border p-3 space-y-2">
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-3 text-sm font-medium cursor-pointer">
          <Checkbox checked={shown} onChange={(e) => onShow(e.target.checked)} className="h-4 w-4" />
          {block.label}
        </label>
        {edited && shown && (
          <button
            type="button"
            onClick={onReset}
            className="ml-auto text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
          >
            Use PagePledge's text
          </button>
        )}
      </div>
      {shown && (
        <>
          <Input
            aria-label={`${block.label} heading`}
            value={value.heading}
            maxLength={limits.heading}
            placeholder={block.default.heading}
            onChange={(e) => onChange("heading", e.target.value)}
          />
          {block.kind === "steps" ? (
            <ol className="space-y-3 pt-1">
              {block.steps.map((stepKey, index) => (
                <li key={stepKey} className="flex gap-2">
                  <span className="mt-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground">
                    {index + 1}
                  </span>
                  <div className="flex-1 space-y-1.5">
                    <Input
                      aria-label={`Step ${index + 1} title`}
                      value={value.steps[stepKey].title}
                      maxLength={limits.step_title}
                      placeholder={block.default.steps[stepKey].title}
                      onChange={(e) => onStepChange(stepKey, "title", e.target.value)}
                    />
                    <AutoTextarea
                      aria-label={`Step ${index + 1} text`}
                      value={value.steps[stepKey].body}
                      maxLength={limits.step_body}
                      onChange={(v) => onStepChange(stepKey, "body", v)}
                    />
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <AutoTextarea
              aria-label={`${block.label} text`}
              value={value.body}
              maxLength={limits.text}
              onChange={(v) => onChange("body", v)}
            />
          )}
          {children}
        </>
      )}
    </div>
  );
}

// Grows with its content so every block is readable without scrolling.
function AutoTextarea({ value, onChange, ...props }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [value]);

  return (
    <Textarea
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="px-3 py-2 text-sm leading-relaxed resize-none overflow-hidden"
      {...props}
    />
  );
}

// Explain toggles that currently have nothing to show.
function toggleHint(key, campaign) {
  if (key === "note" && !campaign.has_description) return "No campaign description set — add one in Edit campaign.";
  if (key === "prizes" && !campaign.has_prizes) return "No prizes set up yet.";
  return null;
}

/**
 * Renders the sheet for the current (unsaved) options, debounced, and
 * lays it out as literal letter pages: every .keep block that would cross
 * a page's bottom margin moves to the top of the next page, the way print
 * does. The server re-checks the real PDF's page count on save.
 */
function SheetPreview({ previewPath, options, marginY, maxPages, onPages }) {
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const [html, setHtml] = useState("");
  const [scale, setScale] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const payload = JSON.stringify(options);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(previewPath, {
          method: "POST",
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
            Accept: "text/html",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
          },
          body: payload,
        });
        if (response.ok) setHtml(await response.text());
      } catch (error) {
        if (error.name !== "AbortError") throw error;
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [previewPath, payload]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setScale(Math.min(1, entry.contentRect.width / PAGE_WIDTH_PX));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const paginate = () => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc?.body) return;
    const blocks = [...doc.body.querySelectorAll(".keep")];
    const printableHeight = PAGE_HEIGHT_PX - 2 * marginY;

    blocks.forEach((el, i) => {
      const rect = el.getBoundingClientRect();
      // Headings travel with the block after them.
      const next = el.classList.contains("keep-with-next") ? blocks[i + 1] : null;
      const needed = next ? next.getBoundingClientRect().bottom - rect.top : rect.height;
      const page = Math.floor(rect.top / PITCH_PX);
      const pageBottom = page * PITCH_PX + PAGE_HEIGHT_PX - marginY;

      if (rect.top + needed > pageBottom && needed <= printableHeight) {
        const shift = (page + 1) * PITCH_PX + marginY - rect.top;
        const current = parseFloat(doc.defaultView.getComputedStyle(el).marginTop) || 0;
        el.style.marginTop = `${current + shift}px`;
      }
    });

    const last = blocks[blocks.length - 1];
    const bottom = last ? last.getBoundingClientRect().bottom : 0;
    const count = Math.max(1, Math.floor((bottom - 1) / PITCH_PX) + 1);
    doc.body.style.minHeight = `${count * PITCH_PX - GAP_PX}px`;
    setPages(count);
    onPages(count);
  };

  // Wait for the web fonts, or line wraps (and so page breaks) shift after.
  const handleLoad = () => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    (doc.fonts?.ready || Promise.resolve()).then(paginate);
  };

  const height = pages * PITCH_PX - GAP_PX;
  const tooLong = pages > maxPages;

  return (
    <Card className="overflow-hidden flex flex-col lg:h-full">
      <CardHeader className="flex-row items-center justify-between space-y-0 py-3 gap-3 shrink-0">
        <CardTitle className="text-base">Preview</CardTitle>
        <PageStatus loading={loading} pages={pages} maxPages={maxPages} />
      </CardHeader>
      <CardContent className="bg-secondary/40 p-4 min-h-0 flex-1 lg:overflow-y-auto">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Page 1 · front
        </p>
        <div ref={containerRef} className="w-full">
          <div style={{ width: PAGE_WIDTH_PX * scale, height: height * scale }} className="relative mx-auto">
            <iframe
              ref={iframeRef}
              title="Sheet preview"
              srcDoc={html}
              sandbox="allow-same-origin"
              onLoad={handleLoad}
              scrolling="no"
              style={{ width: PAGE_WIDTH_PX, height, transform: `scale(${scale})`, transformOrigin: "top left" }}
              className="absolute left-0 top-0 border-0 shadow-md"
            />
            {Array.from({ length: pages - 1 }, (_, i) => i + 1).map((index) => (
              <div key={index}>
                <div
                  className={`absolute left-0 right-0 flex items-center justify-center text-[11px] font-semibold uppercase tracking-wider ${
                    index >= maxPages ? "text-destructive" : "text-muted-foreground"
                  }`}
                  style={{ top: (index * PITCH_PX - GAP_PX) * scale, height: GAP_PX * scale }}
                >
                  {index >= maxPages ? `Page ${index + 1} · won't fit on one sheet` : `Page ${index + 1} · back of the sheet`}
                </div>
                {index >= maxPages && (
                  <div
                    className="absolute left-0 right-0 bg-destructive/15 ring-2 ring-destructive pointer-events-none"
                    style={{ top: index * PITCH_PX * scale, height: PAGE_HEIGHT_PX * scale }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
        {tooLong && (
          <p className="mt-3 text-sm text-destructive">
            This needs {pages} pages. A sheet can use {maxPages} at most — one piece of paper, both sides.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function PageStatus({ loading, pages, maxPages }) {
  if (loading) return <span className="text-xs text-muted-foreground">Updating…</span>;
  if (pages > maxPages) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-destructive">
        <AlertTriangle className="h-3.5 w-3.5" /> Too long — {pages} pages
      </span>
    );
  }
  if (pages === 2) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-warning-foreground">
        <AlertTriangle className="h-3.5 w-3.5" /> Continues onto the back — print double-sided
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success">
      <CheckCircle2 className="h-3.5 w-3.5" /> Fits on one page
    </span>
  );
}
